import { useTheme } from '@emotion/react';
import { Flex } from '@mantine/core';
import { IconCheck, IconInfoCircle } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';
import { useContext } from 'react';

import { Title } from '~/domains/design-system/Title';
import { Button } from '~/domains/design-system/Button';
import dt from '~/testing';
import type { BonusModalTemplateProps } from '~/domains/promotions/domains/promoCodes/components/PromotionBonusModal/types';
import { PromotionsModalsContext } from '~/domains/promotions/Modals/PromotionsModalsContext';

interface RegistrationPromoCodeBannerProps {
  isLoading?: boolean;
  codeError?: string;
  referralCode?: string;
  promoCode?: string;
  promoModalProps?: Omit<BonusModalTemplateProps, 'onAction' | 'isOpen' | 'onClose'>;
}

function RenderBannerContent({ isLoading, codeError, referralCode, promoCode }: Omit<RegistrationPromoCodeBannerProps, 'promoModalProps'>) {
  const theme = useTheme();
  const { t } = useTranslation('auth');

  if (isLoading) {
    return (<Title variant="subhead-small" color={theme.colors.gray2[6]}>
      {t('signUp.promoCode.loading')}
    </Title>)
  }
  if (codeError) {
    return (<Title variant="subhead-small" color={theme.colors.red[6]}>
      {codeError}
    </Title>)
  }

  if (referralCode) {
    return (<Title variant="subhead-small" color={theme.colors.green[6]}>
      {t('signUp.promoCode.referralBanner', { code: referralCode })}
    </Title>)
  }
  if (promoCode) {
    return (<Title variant="subhead-small" color={theme.colors.green[6]}>
      {t('signUp.promoCode.promoBanner', { code: promoCode })}
    </Title>)
  }

  return null;
}

export function RegistrationPromoCodeBanner({
  promoModalProps,
  codeError,
  isLoading,
  referralCode,
  promoCode,
}: RegistrationPromoCodeBannerProps) {
  const theme = useTheme();
  const { openModalUnique, closeModalsByType } = useContext(PromotionsModalsContext);

  const handleOpenPromoModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    openModalUnique({
      type: 'PromoBonusModal'  as const,
      ...promoModalProps,
      onAction: () => {
        closeModalsByType('PromoBonusModal');
      },
    });
  };

  return (
    <Flex align="center" gap={theme.other.spacing._6} justify="center" mt={theme.other.spacing._32} data-test-id={dt.components.shared.auth.signUp.promoCodeBanner}>
      {!isLoading && !codeError && (referralCode || promoCode)
        ? <IconCheck size={20} strokeWidth={3.25} color={theme.colors.green[6]} />
        : null
      }
      <Title variant="subhead-small" color={isLoading ? theme.colors.gray2[6] : theme.colors.green[6]}>
        {RenderBannerContent({ isLoading, codeError, referralCode, promoCode })}
      </Title>
      {promoModalProps && (
        <Button
          variant="text"
          size="x-small-icon"
          onClick={handleOpenPromoModal}
          >
            <IconInfoCircle
              size={20}
              strokeWidth={1.25}
              color={theme.colors.gray2[3]}
            />
        </Button>
      )}
    </Flex>
  );
}
